import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import HCaptcha from "@hcaptcha/react-hcaptcha";
import axios from "axios";

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    service: '',
    budget: '',
    echeance: '',
    description: '',
    rgpd: false,
  });

  const [submitting, setSubmitting] = useState(false);

  // hCaptcha token
  const [captchaToken, setCaptchaToken] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const onHCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations minimales
    if (!formData.nom || !formData.email || !formData.telephone || !formData.service) {
      toast.error("Veuillez remplir tous les champs obligatoires (*)");
      return;
    }

    if (!formData.rgpd) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setSubmitting(true);

    const web3Payload = {
      access_key: "85d1b3f5-d84b-42de-86f0-8fcb513bba11",
      name: formData.nom,
      email: formData.email,
      tel: formData.telephone,
      entreprise: formData.entreprise,
      service: formData.service,
      budget: formData.budget,
      echeance: formData.echeance,
      description: formData.description,
      subject: `Demande de devis - ${formData.service || formData.nom || "Contact"}`,
      "h-captcha-response": captchaToken,
      redirect: "",
    };

    try {
      const response = await axios.post("https://api.web3forms.com/submit", web3Payload);

      if (response?.data?.success) {
        toast.success("✅ Message envoyé avec succès!", {
          description: "Nous vous répondrons dans les plus brefs délais.",
        });

        setFormData({
          nom: '',
          email: '',
          telephone: '',
          entreprise: '',
          service: '',
          budget: '',
          echeance: '',
          description: '',
          rgpd: false,
        });
        setCaptchaToken(null);
        return;
      }

      toast.error("Une erreur est survenue lors de l'envoi (Web3Forms).");
    } catch (err) {
      toast.error("Une erreur est survenue lors de l'envoi (Web3Forms).");
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    'Développement logiciel',
    'Data & Analytics',
    'Transformation digitale',
    'Application mobile',
    'Plateforme web',
    'Consulting',
    'Autre',
  ];

  const budgets = [
    'Moins de 2 000€',
    '2 000 - 5 000€',
    '5 000 - 10 000€',
    '10 000 - 15 000€',
    'Plus de 15 000€',
    'À définir',
  ];

  return (
    <div>
      <Toaster position="top-right" richColors />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-cyan-50 to-blue-50 py-24 sm:py-32">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <Mail size={16} />
              <span>Contactez-nous</span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Parlons de votre projet
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 leading-relaxed"
            >
              Vous avez un projet en tête ? Nous sommes là pour vous écouter et vous accompagner.
              Remplissez le formulaire ci-dessous et recevez un devis personnalisé.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1 space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Informations de contact</h2>
                <p className="text-gray-600 leading-relaxed">
                  Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre projet.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a
                      href="mailto:contact@kynoviatech.com"
                      className="text-gray-600 hover:text-cyan-600 transition-colors"
                    >
                      contact@kynoviatech.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                    <a
                      href="tel:+33668466995"
                      className="text-gray-600 hover:text-cyan-600 transition-colors"
                    >
                      +33 6 68 46 69 95
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                    <p className="text-gray-600">
                      12 Rue de la Plaine<br />
                      75000 Paris, France
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-2xl border border-cyan-200">
                <h3 className="font-semibold text-gray-900 mb-2">Horaires d'ouverture</h3>
                <p className="text-gray-600 text-sm">
                  Lundi - Vendredi: 9h00 - 18h00<br />
                  Samedi - Dimanche: Fermé
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-gray-200 shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Demande de devis</h2>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      required
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="jean.dupont@exemple.fr"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      required
                      value={formData.telephone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label htmlFor="entreprise" className="block text-sm font-medium text-gray-700 mb-2">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      id="entreprise"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service souhaité *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                    >
                      <option value="">Sélectionnez un service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                      Budget estimé
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                    >
                      <option value="">Sélectionnez un budget</option>
                      {budgets.map((budget) => (
                        <option key={budget} value={budget}>
                          {budget}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="echeance" className="block text-sm font-medium text-gray-700 mb-2">
                    Échéance souhaitée
                  </label>
                  <input
                    type="date"
                    id="echeance"
                    name="echeance"
                    value={formData.echeance}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description du projet
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={6}
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none transition-all resize-none"
                    placeholder="Décrivez votre projet en quelques lignes..."
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-start space-x-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="rgpd"
                      checked={formData.rgpd}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-900 transition-colors">
                      J'accepte que mes données soient utilisées dans le cadre de ma demande et de la relation commerciale qui peut en découler.
                      Conformément au RGPD, vous disposez d'un droit d'accès, de modification et de suppression de vos données. *
                    </span>
                  </label>
                </div>

                {/* ✅ hCaptcha */}
                <div className="mb-6">
                  <HCaptcha
                    sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                    onVerify={onHCaptchaChange}
                    onExpire={() => setCaptchaToken(null)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-cyan-500/50 transform hover:scale-[1.02] transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Envoyer la demande</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
